/**
 * atipayment — client-side helpers for the aTIProto payment workflow.
 *
 * All real aTIProto XRPC calls are proxied through our API server so that
 * React Native's bundler never needs to resolve the @atiproto/agent package.
 * The mobile client sends the user's Bluesky credentials (over HTTPS) and
 * receives back URLs / status responses.
 *
 * Flow for bill purchases:
 *   1. initiateCheckout()  → returns { checkoutUrl, cartUri }
 *   2. open checkoutUrl in system browser (WebBrowser.openBrowserAsync)
 *   3. user pays via Stripe
 *   4. user returns to app; call fulfillPayment()
 *   5. on success: call useBills().addBills(result.bills)
 *
 * Flow for teacher cashout:
 *   1. getBrokerStatus() → if not ready, open onboardingUrl
 *   2. when ready, call requestWithdrawal() with amount
 */
export interface SavedAccount {
  id: string
  handle: string
  isBluesky: boolean
  did?: string
  accessJwt?: string
  refreshJwt?: string
  pds?: string
}

// ── API base URL ──────────────────────────────────────────────────────────────
import {BSKY_SERVICE} from '#/lib/constants'

const API_BASE = `${BSKY_SERVICE}/api`

// ── Types ─────────────────────────────────────────────────────────────────────
export interface AtipayCreds {
  did: string
  handle: string
  accessJwt: string
  refreshJwt: string
  pds?: string
}

export type CartStatus =
  | 'pending'
  | 'authorized'
  | 'completed'
  | 'failed'
  | 'refunded'
  | 'cancelled'

export interface CheckoutResult {
  checkoutUrl: string
  cartUri: string
}

export interface CartStatusResult {
  status: CartStatus
}

export interface FulfillResult {
  bills: number
  status: CartStatus
  alreadyFulfilled?: boolean
}

export interface BrokerStatusResult {
  status:
    | 'none'
    | 'pending'
    | 'needs_info'
    | 'ready'
    | 'restricted'
    | 'disabled'
  ready: boolean
  onboardingUrl?: string
  acceptsItems?: boolean
  acceptsSubscriptions?: boolean
}

// ── Credential helpers ────────────────────────────────────────────────────────

/**
 * Extracts aTIProto-compatible credentials from a saved Bluesky account.
 * Returns null for demo (non-Bluesky) accounts.
 */
export function extractCreds(account: SavedAccount): AtipayCreds | null {
  if (
    !account.isBluesky ||
    !account.did ||
    !account.accessJwt ||
    !account.refreshJwt
  ) {
    return null
  }
  return {
    did: account.did,
    handle: account.handle.replace(/^@/, ''),
    accessJwt: account.accessJwt,
    refreshJwt: account.refreshJwt,
  }
}

// ── Internal fetch helpers ────────────────────────────────────────────────────

async function apiPost<T>(
  path: string,
  body: object,
  accessJwt: string,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessJwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  const json = (await res.json()) as {error?: string} & T
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`)
  return json
}

async function apiGet<T>(
  path: string,
  params: Record<string, string>,
  accessJwt: string,
): Promise<T> {
  const qs = new URLSearchParams(params).toString()
  const res = await fetch(`${API_BASE}${path}?${qs}`, {
    headers: {Authorization: `Bearer ${accessJwt}`},
  })
  const json = (await res.json()) as {error?: string} & T
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`)
  return json
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Step 1 — Create a Stripe checkout session for a bill package.
 * Returns the Stripe-hosted checkoutUrl and the aTIProto cartUri (needed for fulfillment).
 */
export async function initiateCheckout(
  creds: AtipayCreds,
  packageId: string,
  userId: string,
): Promise<CheckoutResult> {
  return apiPost<CheckoutResult>(
    '/payment/checkout',
    {
      ...creds,
      packageId,
      userId,
    },
    creds.accessJwt,
  )
}

/**
 * Step 2 (optional polling) — Check the current status of a cart.
 */
export async function getCartStatus(
  creds: AtipayCreds,
  cartUri: string,
): Promise<CartStatusResult> {
  return apiPost<CartStatusResult>(
    '/payment/status',
    {...creds, cartUri},
    creds.accessJwt,
  )
}

/**
 * Step 3 — Verify the payment on the server and get the bills credit.
 * Server checks aTIProto cart status; if "completed" returns bills count.
 * Call addBills(result.bills) from BillsContext after this.
 */
export async function fulfillPayment(
  creds: AtipayCreds,
  cartUri: string,
  packageId: string,
  userId: string,
): Promise<FulfillResult> {
  return apiPost<FulfillResult>(
    '/payment/fulfill',
    {
      ...creds,
      cartUri,
      packageId,
      userId,
    },
    creds.accessJwt,
  )
}

/**
 * Teacher — check broker onboarding status.
 * If not ready, follow onboardingUrl to complete Stripe Connect setup.
 */
export async function getBrokerStatus(
  creds: AtipayCreds,
): Promise<BrokerStatusResult> {
  return apiGet<BrokerStatusResult>(
    '/payment/broker',
    {
      refreshJwt: creds.refreshJwt,
      did: creds.did,
      handle: creds.handle,
      ...(creds.pds ? {pds: creds.pds} : {}),
    },
    creds.accessJwt,
  )
}

/**
 * Teacher — request a withdrawal of earnings.
 * Only callable when broker status is "ready".
 */
export async function requestWithdrawal(
  creds: AtipayCreds,
  userId: string,
  amountEur: number,
): Promise<{success: boolean; message: string}> {
  return apiPost(
    '/payment/withdraw',
    {
      ...creds,
      userId,
      amountEur,
    },
    creds.accessJwt,
  )
}
